import { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@altimateai/lego";
import {
  OwnershipEntities,
  OwnershipConditionKeys,
  OwnershipConditionTypes,
  OwnershipRulesFormValues,
} from "./types";
import { getConditionKeysForEntity, getConditionTypesForKey } from "./ownershipHelpers";

interface OwnershipFormFieldsProps {
  form: UseFormReturn<OwnershipRulesFormValues>;
  index: number;
  children?: React.ReactNode;
}

const OwnershipFormFields = ({ form, index, children }: OwnershipFormFieldsProps) => {
  const ownershipEntityType = form.watch(`ownerships.${index}.entity_type`);
  const conditionKey = form.watch(`ownerships.${index}.condition_key`);

  // Handle condition_key validation and updates when entity_type changes
  useEffect(() => {
    const currentKeyValue = form.getValues(`ownerships.${index}.condition_key`);
    const availableKeys = getConditionKeysForEntity(ownershipEntityType);

    const validValue = availableKeys.includes(currentKeyValue) ? currentKeyValue : availableKeys[0];

    if (validValue !== currentKeyValue) {
      form.setValue(`ownerships.${index}.condition_key`, validValue);
      const availableTypes = getConditionTypesForKey(validValue);
      const defaultType = availableTypes[0] || OwnershipConditionTypes.EQUALS;
      form.setValue(`ownerships.${index}.condition_type`, defaultType);
      form.setValue(`ownerships.${index}.condition_value`, "");
      form.setValue(`ownerships.${index}.condition_key_value`, "");
    }
  }, [ownershipEntityType, form, index]);

  // Handle condition_type validation and updates when condition_key changes
  useEffect(() => {
    const currentTypeValue = form.getValues(`ownerships.${index}.condition_type`);
    const availableTypes = getConditionTypesForKey(conditionKey);

    const validValue = availableTypes.includes(currentTypeValue)
      ? currentTypeValue
      : availableTypes[0];

    if (validValue !== currentTypeValue) {
      form.setValue(`ownerships.${index}.condition_type`, validValue);
      form.setValue(`ownerships.${index}.condition_value`, "");
      form.setValue(`ownerships.${index}.condition_key_value`, "");
    }
  }, [conditionKey, form, index]);

  return (
    <>
      <div className="al-grid al-grid-cols-2 al-gap-4 al-mt-2">
        <FormField
          control={form.control}
          name={`ownerships.${index}.entity_type`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Select entity type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="al-w-full">
                    <SelectValue placeholder="Select entity type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.values(OwnershipEntities).map(type => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={`ownerships.${index}.condition_key`}
          render={({ field }) => {
            const ownershipEntityType = form.watch(`ownerships.${index}.entity_type`);
            const availableKeys = getConditionKeysForEntity(ownershipEntityType);

            return (
              <FormItem>
                <FormLabel>Select match type</FormLabel>
                <Select
                  onValueChange={(value: string) => {
                    field.onChange(value);
                    const availableTypes = getConditionTypesForKey(value as OwnershipConditionKeys);
                    const defaultType = availableTypes[0] || OwnershipConditionTypes.EQUALS;
                    form.setValue(`ownerships.${index}.condition_type`, defaultType);
                    form.setValue(`ownerships.${index}.condition_value`, "");
                    form.setValue(`ownerships.${index}.condition_key_value`, "");
                  }}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="al-w-full">
                      <SelectValue placeholder="Select condition key" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {availableKeys.map(key => (
                      <SelectItem key={key} value={key}>
                        {key}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      </div>

      <div className="al-grid al-grid-cols-2 al-gap-4 al-mt-2">
        <FormField
          control={form.control}
          name={`ownerships.${index}.condition_type`}
          render={({ field }) => {
            const conditionKey = form.watch(`ownerships.${index}.condition_key`);
            const availableTypes = getConditionTypesForKey(conditionKey);

            return (
              <FormItem>
                <FormLabel>Condition type</FormLabel>
                <Select
                  onValueChange={(value: string) => {
                    field.onChange(value);
                    form.setValue(`ownerships.${index}.condition_value`, "");
                    form.setValue(`ownerships.${index}.condition_key_value`, "");
                  }}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="al-w-full">
                      <SelectValue placeholder="Select condition type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {availableTypes.map(type => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <div className="al-mt-0">{children}</div>
      </div>
    </>
  );
};

export default OwnershipFormFields;
